import React from 'react';

// Line-stroke SVG icon set used throughout the app.
// All icons share the same style as SearchPanel's magnifying-glass and
// Lobby's catch-up button: 24x24 viewBox, fill="none", stroke="currentColor".
// `size` defaults to 16 — pass a different value or set `width`/`height`
// via CSS on the parent if you need to override.

function Icon({ children, size = 16, strokeWidth = 2, fill = 'none', ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill={fill}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {children}
    </svg>
  );
}

export function IconSearch(props) {
  return (
    <Icon {...props}>
      <circle cx="11" cy="11" r="7" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </Icon>
  );
}

// Fast-forward — matches the inline SVG already used by Lobby's catch-up button.
export function IconCatchUp(props) {
  return (
    <Icon fill="currentColor" strokeWidth={0} {...props}>
      <polygon points="3,4 14,12 3,20" />
      <polygon points="13,4 24,12 13,20" />
    </Icon>
  );
}

// Trophy — the settled outcome / win, the decision symbol. Cup + handles on a
// stepped pedestal base (the pedestal is what reads as "trophy" not "cup").
export function IconDecision(props) {
  return (
    <Icon {...props}>
      <path d="M6 9H4.5a2.5 2.5 0 0 1 0-5H6" />
      <path d="M18 9h1.5a2.5 2.5 0 0 0 0-5H18" />
      <path d="M18 2H6v7a6 6 0 0 0 12 0V2Z" />
      <path d="M12 15v3" />
      <path d="M9.5 18h5" />
      <path d="M7.5 22h9l-1.5-4h-6l-1.5 4Z" />
    </Icon>
  );
}

// Pull-request-style branch graph, for the Decision Graph. A straight "base"
// track on the left and a branch that curves up to a source arrow on the right.
export function IconGraph(props) {
  return (
    <Icon {...props}>
      {/* Left "base" track: two nodes joined by a straight line. */}
      <circle cx="6" cy="6" r="2.5" />
      <circle cx="6" cy="18" r="2.5" />
      <line x1="6" y1="8.5" x2="6" y2="15.5" />
      {/* Right branch: merge node, curving up to the branch source. */}
      <circle cx="18" cy="18" r="2.5" />
      <path d="M18 15.5 V9 a3 3 0 0 0 -3 -3 H13" />
      <polyline points="14.5 4.5 12.5 6 14.5 7.5" />
    </Icon>
  );
}

// Document — for artifacts.
export function IconArtifact(props) {
  return (
    <Icon {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="8" y1="13" x2="16" y2="13" />
      <line x1="8" y1="17" x2="14" y2="17" />
    </Icon>
  );
}

// Checkbox with checkmark — for todos.
export function IconTodo(props) {
  return (
    <Icon {...props}>
      <polyline points="9 11 12 14 20 6" />
      <path d="M20 12v7a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2h11" />
    </Icon>
  );
}

// Chat bubble — for messages.
export function IconMessage(props) {
  return (
    <Icon {...props}>
      <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
    </Icon>
  );
}

// Chain-link glyph — for a "go to source / provenance" link affordance.
export function IconLink(props) {
  return (
    <Icon {...props}>
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </Icon>
  );
}

// Two-person glyph — for participants/People panel toggle.
export function IconPeople(props) {
  return (
    <Icon {...props}>
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </Icon>
  );
}

// Side-panel / board — for the artifacts/todos right panel toggle.
export function IconBoard(props) {
  return (
    <Icon {...props}>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="15" y1="3" x2="15" y2="21" />
    </Icon>
  );
}

// Bar chart — for workspace statistics.
export function IconChart(props) {
  return (
    <Icon {...props}>
      <line x1="3" y1="21" x2="21" y2="21" />
      <rect x="5" y="12" width="4" height="7" />
      <rect x="11" y="8" width="4" height="11" />
      <rect x="17" y="4" width="4" height="15" />
    </Icon>
  );
}

// Vertical kebab — for the overflow menu.
export function IconMore(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="5" r="1" />
      <circle cx="12" cy="12" r="1" />
      <circle cx="12" cy="19" r="1" />
    </Icon>
  );
}

// Download arrow — for the Export menu item.
export function IconExport(props) {
  return (
    <Icon {...props}>
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
      <polyline points="7 10 12 15 17 10" />
      <line x1="12" y1="15" x2="12" y2="3" />
    </Icon>
  );
}

// Target / crosshair — for the thread objective.
export function IconObjective(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.5" fill="currentColor" strokeWidth="0" />
    </Icon>
  );
}

// Corner-down-left arrow — for "Reply".
export function IconReply(props) {
  return (
    <Icon {...props}>
      <polyline points="9 14 4 9 9 4" />
      <path d="M20 20v-7a4 4 0 0 0-4-4H4" />
    </Icon>
  );
}

// Smile + plus — for opening the reaction picker.
export function IconReact(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="9" />
      <path d="M8 14s1.5 2 4 2 4-2 4-2" />
      <line x1="9" y1="9" x2="9.01" y2="9" />
      <line x1="15" y1="9" x2="15.01" y2="9" />
    </Icon>
  );
}

// Sparkles — for "Ask AI".
export function IconAskAI(props) {
  return (
    <Icon {...props}>
      <path d="M12 3v3" />
      <path d="M12 18v3" />
      <path d="M3 12h3" />
      <path d="M18 12h3" />
      <path d="M5.6 5.6l2.1 2.1" />
      <path d="M16.3 16.3l2.1 2.1" />
      <path d="M5.6 18.4l2.1-2.1" />
      <path d="M16.3 7.7l2.1-2.1" />
    </Icon>
  );
}

// Pencil — for "Edit".
export function IconEdit(props) {
  return (
    <Icon {...props}>
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4 12.5-12.5z" />
    </Icon>
  );
}

// Settings gear — for the workspace settings menu.
export function IconSettings(props) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
    </Icon>
  );
}

// Plus — for "create / add".
export function IconPlus(props) {
  return (
    <Icon {...props}>
      <line x1="12" y1="5" x2="12" y2="19" />
      <line x1="5" y1="12" x2="19" y2="12" />
    </Icon>
  );
}

// Pin — for "Pin/Unpin".
export function IconPin(props) {
  return (
    <Icon {...props}>
      <line x1="12" y1="17" x2="12" y2="22" />
      <path d="M9 2h6l-1 7 4 4v3H6v-3l4-4-1-7z" />
    </Icon>
  );
}

// Trash — for "Delete".
export function IconTrash(props) {
  return (
    <Icon {...props}>
      <polyline points="3 6 5 6 21 6" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6" />
      <path d="M14 11v6" />
      <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
    </Icon>
  );
}

// Markdown file — for the Markdown export option.
export function IconMarkdown(props) {
  return (
    <Icon {...props}>
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <path d="M8 13v5" />
      <path d="M8 13l2 2 2-2" />
      <path d="M12 18v-5" />
      <path d="M16 13v5" />
    </Icon>
  );
}

// Braces — for the JSON export option.
export function IconJson(props) {
  return (
    <Icon {...props}>
      <path d="M8 3H6a2 2 0 0 0-2 2v4a2 2 0 0 1-2 2 2 2 0 0 1 2 2v4a2 2 0 0 0 2 2h2" />
      <path d="M16 3h2a2 2 0 0 1 2 2v4a2 2 0 0 0 2 2 2 2 0 0 0-2 2v4a2 2 0 0 1-2 2h-2" />
    </Icon>
  );
}

// X — for close affordances.
export function IconClose(props) {
  return (
    <Icon {...props}>
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </Icon>
  );
}

// Left arrow — for the back button.
export function IconBack(props) {
  return (
    <Icon {...props}>
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </Icon>
  );
}

// Sign-out — door with an arrow leaving, for the log-out action.
export function IconLogout(props) {
  return (
    <Icon {...props}>
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
      <polyline points="16 17 21 12 16 7" />
      <line x1="21" y1="12" x2="9" y2="12" />
    </Icon>
  );
}

// Chevron-down — for the scroll-to-bottom affordance.
export function IconChevronDown(props) {
  return (
    <Icon {...props}>
      <polyline points="6 9 12 15 18 9" />
    </Icon>
  );
}

// Chevron-left — collapse/expand affordance for the left pane.
export function IconChevronLeft(props) {
  return (
    <Icon {...props}>
      <polyline points="15 18 9 12 15 6" />
    </Icon>
  );
}

// Chevron-right — collapse/expand affordance for the right pane.
export function IconChevronRight(props) {
  return (
    <Icon {...props}>
      <polyline points="9 18 15 12 9 6" />
    </Icon>
  );
}

// Bell — for the notification toggle.
export function IconBell(props) {
  return (
    <Icon {...props}>
      <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
    </Icon>
  );
}

// Bell with a slash — muted AI participant.
export function IconBellOff(props) {
  return (
    <Icon {...props}>
      <path d="M8.7 3A6 6 0 0 1 18 8c0 4.5 1.2 6.7 2.2 8" />
      <path d="M17 17H3s3-2 3-9c0-.7.1-1.4.3-2" />
      <path d="M13.73 21a2 2 0 0 1-3.46 0" />
      <line x1="1" y1="1" x2="23" y2="23" />
    </Icon>
  );
}

// Star — marks/sets the primary AI. Pass `fill="currentColor"` when active.
export function IconStar(props) {
  return (
    <Icon {...props}>
      <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
    </Icon>
  );
}

// Three little sparkles in a row — kept for backward compat.
export function IconStars({ size = 16, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      <path d="M5 4.5l.7 1.6 1.6.7-1.6.7L5 9.1l-.7-1.6-1.6-.7 1.6-.7L5 4.5z" />
      <path d="M12 9l1 2.4 2.4 1-2.4 1-1 2.4-1-2.4-2.4-1 2.4-1L12 9z" />
      <path d="M19 14.9l.7 1.6 1.6.7-1.6.7-.7 1.6-.7-1.6-1.6-.7 1.6-.7.7-1.6z" />
    </svg>
  );
}

// Three 4-pointed sparkles of different sizes — Aimy's avatar icon (✨ style).
// Large lower-left, medium upper-right, small lower-right — matches the ✨ emoji arrangement.
export function IconSparkle({ size = 16, ...rest }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      aria-hidden="true"
      focusable="false"
      {...rest}
    >
      {/* Large — center (8,15), R=6, inner offset d=1.3 */}
      <path d="M8,9 L9.3,13.7 L14,15 L9.3,16.3 L8,21 L6.7,16.3 L2,15 L6.7,13.7 Z" />
      {/* Medium — center (17,7), R=4, inner offset d=0.9 */}
      <path d="M17,3 L17.9,6.1 L21,7 L17.9,7.9 L17,11 L16.1,7.9 L13,7 L16.1,6.1 Z" />
      {/* Small — center (19,17), R=2.5, inner offset d=0.6 */}
      <path d="M19,14.5 L19.6,16.4 L21.5,17 L19.6,17.6 L19,19.5 L18.4,17.6 L16.5,17 L18.4,16.4 Z" />
    </svg>
  );
}

// Hash — for thread-scoped search.
export function IconHash(props) {
  return (
    <Icon {...props}>
      <line x1="4" y1="9" x2="20" y2="9" />
      <line x1="4" y1="15" x2="20" y2="15" />
      <line x1="10" y1="3" x2="8" y2="21" />
      <line x1="16" y1="3" x2="14" y2="21" />
    </Icon>
  );
}

// Building — for workspace-scoped search.
export function IconWorkspace(props) {
  return (
    <Icon {...props}>
      <rect x="3" y="9" width="18" height="12" rx="1" />
      <path d="M3 9l9-6 9 6" />
      <line x1="9" y1="21" x2="9" y2="14" />
      <line x1="15" y1="21" x2="15" y2="14" />
    </Icon>
  );
}

// Paperclip — for "Attach a file".
export function IconPaperclip(props) {
  return (
    <Icon {...props}>
      <path d="M21.44 11.05l-9.19 9.19a5 5 0 0 1-7.07-7.07l8.49-8.49a3 3 0 0 1 4.24 4.24l-8.49 8.49a1 1 0 0 1-1.41-1.41l7.78-7.78" />
    </Icon>
  );
}

// Paper aeroplane — for "Send".
export function IconSend(props) {
  return (
    <Icon {...props}>
      <line x1="22" y1="2" x2="11" y2="13" />
      <polygon points="22 2 15 22 11 13 2 9 22 2" />
    </Icon>
  );
}

// Inward-collapsing chevrons — fold/compress, for "Compact context".
export function IconCompact(props) {
  return (
    <Icon {...props}>
      <polyline points="4 8 8 12 4 16" />
      <polyline points="20 8 16 12 20 16" />
      <line x1="11" y1="12" x2="13" y2="12" />
    </Icon>
  );
}

// Share — a node with two outgoing connections, the standard share glyph.
// Used by the decision-card "Share" affordance (public decision share links).
export function IconShare(props) {
  return (
    <Icon {...props}>
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
    </Icon>
  );
}
